/**
 * Strategy Configuration
 * Defines display properties, metrics, and behavior for each investment strategy
 * 
 * NOTE: All default assumptions based on 2025 market research:
 * - Growth rates: Savills July 2025 forecast (revised from 4% to 1% for 2025)
 * - Selling costs: The Advisory 2025 survey (1.18% + VAT avg agent fees)
 * - BTL assumptions: Industry benchmarks (Portico, Cribs Estate, Shergroup)
 */

// ============================================================================
// REAL-WORLD MARKET ASSUMPTIONS (Research-Backed)
// ============================================================================

/**
 * Annual property price growth rates by location tier
 * Source: Savills July 2025 forecast, Knight Frank, Nationwide
 * UK national avg: 1-3.5% (2025), London underperforming at 0-2%
 */
export const ANNUAL_GROWTH_RATES = {
  LONDON_PRIME_CENTRAL: 0.015,  // 1.5% - subdued prime market (Zone 1)
  LONDON_ZONE_1_2: 0.02,        // 2% - modest growth (Zone 1-2)
  LONDON_OUTER: 0.025,          // 2.5% - better value (Zone 3-6)
  UK_NATIONAL: 0.025,           // 2.5% - national average (Savills 2025)
  UK_REGIONAL_GROWTH: 0.035,    // 3.5% - north/midlands hotspots
};

/**
 * Default growth rate when location unknown
 * Conservative estimate based on 2025 forecasts (1-3.5% range, avg ~2.5%)
 */
export const DEFAULT_GROWTH_RATE = 0.025; // 2.5%

/**
 * Property selling costs breakdown
 * Source: The Advisory 2025 survey (2,000+ sellers)
 * - Estate agents: 1.18% + VAT = 1.42% average
 * - Total typical: ~3% including legal/EPC
 */
export const SELLING_COSTS = {
  ESTATE_AGENT_PCT: 0.012,      // 1.2% base fee (sole agency)
  VAT_MULTIPLIER: 1.20,         // 20% VAT on agent fees
  LEGAL_FEES_FIXED: 800,        // £800 conveyancing
  EPC_FIXED: 85,                // £85 EPC certificate
  get TOTAL_PCT() {             // ~3.0% for typical £300k property
    return (this.ESTATE_AGENT_PCT * this.VAT_MULTIPLIER) + 0.015; // +1.5% for legal/misc
  }
};

/**
 * Buy-to-Let operating assumptions
 * Sources: Portico, Cribs Estate, Shergroup risk-adjusted models
 */
export const BTL_ASSUMPTIONS = {
  STANDARD: {
    void_allowance: 0.06,       // 6% (3-4 weeks/year void)
    maintenance: 0.08,          // 8% of rent (1% of property value annually)
    management: 0.10,           // 10% of rent (industry standard)
    mortgage_rate: 0.055,       // 5.5% BTL mortgage (current market)
  },
  CHALLENGING_AREA: {
    void_allowance: 0.08,       // 8% (current default - higher risk areas)
    maintenance: 0.10,          // 10% of rent
    management: 0.12,           // 12% of rent
    mortgage_rate: 0.055,       // 5.5% BTL mortgage
  }
};

/**
 * Get location-appropriate growth rate
 * @param {string} locationTier - 'prime_central', 'zone_1_2', 'outer_london', 'regional'
 * @returns {number} Annual growth rate as decimal
 */
export const getGrowthRateForLocation = (locationTier) => {
  const rates = {
    'prime_central': ANNUAL_GROWTH_RATES.LONDON_PRIME_CENTRAL,
    'zone_1_2': ANNUAL_GROWTH_RATES.LONDON_ZONE_1_2,
    'outer_london': ANNUAL_GROWTH_RATES.LONDON_OUTER,
    'regional_growth': ANNUAL_GROWTH_RATES.UK_REGIONAL_GROWTH,
    'national': ANNUAL_GROWTH_RATES.UK_NATIONAL,
  };
  return rates[locationTier] || DEFAULT_GROWTH_RATE;
};

// ============================================================================
// STRATEGY DEFINITIONS
// ============================================================================
//
// Labelling convention:
// - Landlord strategies (BTL, HMO, STL): comparison tiles and charts use "cash-on-cash (Y1)"
//   and "cash to complete" where those metrics are shown (see StrategySelector / StrategyComparison).
// - Project strategies (fix & flip, development, commercial conversion): DynamicMetrics and
//   charts keep "Project ROI" and "Capital required" — estimated_roi_percent / initial_capital_required
//   mean project return on equity and total equity/cash before finance, not mortgage LTV.

export const STRATEGY_TYPES = {
  BUY_TO_LET: 'buy_to_let',
  FIX_AND_FLIP: 'fix_and_flip',
  HMO_CONVERSION: 'hmo_conversion',
  SHORT_TERM_LET: 'short_term_let',
  DEVELOPMENT: 'development',
  COMMERCIAL_CONVERSION: 'commercial_conversion',
  LAND_BANKING: 'land_banking'
};

/** Strategies with dedicated backend Monte Carlo engines (realism L0–L2). */
export const MONTE_CARLO_STRATEGY_KEYS = new Set([
  STRATEGY_TYPES.BUY_TO_LET,
  STRATEGY_TYPES.HMO_CONVERSION,
  STRATEGY_TYPES.SHORT_TERM_LET,
  STRATEGY_TYPES.FIX_AND_FLIP,
  STRATEGY_TYPES.DEVELOPMENT,
  STRATEGY_TYPES.COMMERCIAL_CONVERSION,
]);

export const STRATEGY_CONFIG = {
  [STRATEGY_TYPES.BUY_TO_LET]: {
    displayName: 'Buy-to-Let',
    shortName: 'BTL',
    icon: '🏠',
    color: 'blue',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-800',
    accentColor: 'bg-blue-500',
    description: 'Long-term rental income strategy with steady cashflow and capital appreciation',
    primaryMetrics: ['gross_yield', 'total_return_annual', 'monthly_cashflow', 'break_even_months'],
    metricLabels: {
      gross_yield: 'Gross yield on price (contract rent)',
      net_yield: 'Net yield on price (after mortgage interest)',
      monthly_cashflow: 'Monthly Cashflow',
      break_even_months: 'Break-even',
      roi_5_year: '5-Year ROI',
      cash_on_cash_return: 'Cash-on-Cash',
      total_investment: 'Total Investment',
      // FIX #8: Total return metrics
      income_return_annual: 'Income Return',
      capital_appreciation_annual: 'Cap. Appreciation',
      total_return_annual: 'Total Return',
      total_return_5_year: '5-Year Total Return'
    },
    metricFormats: {
      gross_yield: (v) => `${v}%`,
      net_yield: (v) => `${v}%`,
      monthly_cashflow: (v) => `£${v.toLocaleString()}`,
      break_even_months: (v) => `${v} mo`,
      roi_5_year: (v) => `${v}%`,
      cash_on_cash_return: (v) => `${v}%`,
      total_investment: (v) => `£${v.toLocaleString()}`,
      // FIX #8: Total return formats
      income_return_annual: (v) => `${v}%`,
      capital_appreciation_annual: (v) => `${v}%`,
      total_return_annual: (v) => `${v}%`,
      total_return_5_year: (v) => `${v}%`
    },
    chartType: 'yield_timeline',
    showMonteCarlo: true,
    showRiskMatrix: true,
    timeframeLabel: 'years',
    riskProfile: 'low_to_moderate'
  },

  [STRATEGY_TYPES.FIX_AND_FLIP]: {
    // Project-style metrics (contrast with landlord strategies above).
    displayName: 'Fix & Flip',
    shortName: 'FLIP',
    icon: '🔨',
    color: 'orange',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-800',
    accentColor: 'bg-orange-500',
    description: 'Purchase, renovate, and sell for profit in 6-12 months',
    primaryMetrics: ['estimated_profit', 'estimated_roi_percent', 'timeline_months', 'initial_capital_required'],
    metricLabels: {
      estimated_profit: 'Est. Profit',
      estimated_roi_percent: 'Project ROI',
      timeline_months: 'Timeline',
      initial_capital_required: 'Capital Required',
      purchase_price: 'Purchase',
      renovation_cost: 'Renovation',
      arv: 'After Repair Value'
    },
    metricFormats: {
      estimated_profit: (v) => `£${v.toLocaleString()}`,
      estimated_roi_percent: (v) => `${v}%`,
      timeline_months: (v) => `${v} mo`,
      initial_capital_required: (v) => `£${v.toLocaleString()}`,
      purchase_price: (v) => `£${v.toLocaleString()}`,
      renovation_cost: (v) => `£${v.toLocaleString()}`,
      arv: (v) => `£${v.toLocaleString()}`
    },
    chartType: 'profit_breakdown',
    showMonteCarlo: false,
    showRiskMatrix: true,
    timeframeLabel: 'months',
    riskProfile: 'moderate_to_high'
  },

  [STRATEGY_TYPES.HMO_CONVERSION]: {
    displayName: 'HMO Conversion',
    shortName: 'HMO',
    icon: '🏢',
    color: 'purple',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-800',
    accentColor: 'bg-purple-500',
    description: 'Convert to multi-let for significantly higher rental yields',
    primaryMetrics: ['gross_yield', 'total_return_annual', 'monthly_cashflow', 'roi_5_year'],
    metricLabels: {
      gross_yield: 'Gross yield on price (contract rent)',
      net_yield: 'Net yield on price (after mortgage interest)',
      monthly_cashflow: 'Monthly Cashflow',
      roi_5_year: '5-Year ROI',
      rooms_created: 'Rooms',
      licensing_required: 'License Required',
      conversion_cost: 'Conversion Cost',
      // FIX #8: Total return metrics
      income_return_annual: 'Income Return',
      capital_appreciation_annual: 'Cap. Appreciation',
      total_return_annual: 'Total Return',
      total_return_5_year: '5-Year Total Return'
    },
    metricFormats: {
      gross_yield: (v) => `${v}%`,
      net_yield: (v) => `${v}%`,
      monthly_cashflow: (v) => `£${v.toLocaleString()}`,
      roi_5_year: (v) => `${v}%`,
      rooms_created: (v) => `${v} rooms`,
      licensing_required: (v) => v ? 'Yes' : 'No',
      conversion_cost: (v) => `£${v.toLocaleString()}`,
      // FIX #8: Total return formats
      income_return_annual: (v) => `${v}%`,
      capital_appreciation_annual: (v) => `${v}%`,
      total_return_annual: (v) => `${v}%`,
      total_return_5_year: (v) => `${v}%`
    },
    chartType: 'yield_comparison',
    showMonteCarlo: true,
    showRiskMatrix: true,
    timeframeLabel: 'years',
    riskProfile: 'moderate'
  },

  [STRATEGY_TYPES.SHORT_TERM_LET]: {
    displayName: 'Short-Term Let',
    shortName: 'STL',
    icon: '✈️',
    color: 'green',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-800',
    accentColor: 'bg-green-500',
    description: 'Airbnb or holiday let strategy for premium yields',
    primaryMetrics: ['gross_yield', 'occupancy_rate', 'monthly_cashflow', 'total_return_annual'],
    metricLabels: {
      gross_yield: 'Gross STL yield on price',
      occupancy_rate: 'Occupancy',
      monthly_cashflow: 'Monthly Cashflow',
      management_costs: 'Management',
      nightly_rate: 'Nightly Rate',
      cleaning_costs: 'Cleaning/mo',
      net_yield: 'Net yield on price (after mortgage interest)',
      setup_costs: 'Setup Costs',
      // FIX #8: Total return metrics
      total_return_annual: 'Total Return',
      total_return_5_year: '5-Year Total Return'
    },
    metricFormats: {
      gross_yield: (v) => `${v}%`,
      occupancy_rate: (v) => `${v}%`,
      monthly_cashflow: (v) => `£${v.toLocaleString()}`,
      management_costs: (v) => `£${v.toLocaleString()}`,
      nightly_rate: (v) => `£${v}`,
      cleaning_costs: (v) => `£${v.toLocaleString()}`,
      setup_costs: (v) => `£${v.toLocaleString()}`,
      // FIX #8: Total return formats
      total_return_annual: (v) => `${v}%`,
      total_return_5_year: (v) => `${v}%`
    },
    chartType: 'seasonal_occupancy',
    showMonteCarlo: true,
    showRiskMatrix: true,
    timeframeLabel: 'years',
    riskProfile: 'moderate'
  },

  [STRATEGY_TYPES.DEVELOPMENT]: {
    displayName: 'Development',
    shortName: 'DEV',
    icon: '🏗️',
    color: 'red',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-800',
    accentColor: 'bg-red-500',
    description: 'New build or major development project',
    primaryMetrics: ['gross_development_value', 'estimated_roi_percent', 'timeline_months', 'initial_capital_required'],
    metricLabels: {
      gross_development_value: 'GDV',
      estimated_roi_percent: 'Project ROI',
      profit_on_cost: 'Profit on Cost',
      timeline_months: 'Timeline',
      initial_capital_required: 'Capital Required',
      build_cost: 'Build Cost',
      planning_status: 'Planning',
      units_created: 'Units'
    },
    metricFormats: {
      gross_development_value: (v) => `£${v.toLocaleString()}`,
      estimated_roi_percent: (v) => `${v}%`,
      profit_on_cost: (v) => `${v}%`,
      timeline_months: (v) => `${v} mo`,
      initial_capital_required: (v) => `£${v.toLocaleString()}`,
      build_cost: (v) => `£${v.toLocaleString()}`,
      planning_status: (v) => v === 'approved' ? '✓ Approved' : v === 'pending' ? '⏳ Pending' : '✗ None',
      units_created: (v) => `${v} units`
    },
    chartType: 'development_phases',
    showMonteCarlo: false,
    showRiskMatrix: true,
    timeframeLabel: 'months',
    riskProfile: 'high'
  },

  [STRATEGY_TYPES.COMMERCIAL_CONVERSION]: {
    displayName: 'Commercial Conversion',
    shortName: 'CONV',
    icon: '🔄',
    color: 'indigo',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    textColor: 'text-indigo-800',
    accentColor: 'bg-indigo-500',
    description: 'Change of use from commercial to residential (Permitted Development)',
    primaryMetrics: ['estimated_profit', 'estimated_roi_percent', 'timeline_months', 'initial_capital_required'],
    metricLabels: {
      estimated_profit: 'Est. Profit',
      estimated_roi_percent: 'Project ROI',
      planning_risk_score: 'Planning Risk',
      timeline_months: 'Timeline',
      initial_capital_required: 'Capital Required',
      conversion_cost_psf: 'Cost/sqft',
      permitted_development: 'PD Rights',
      units_created: 'Units'
    },
    metricFormats: {
      estimated_profit: (v) => `£${v.toLocaleString()}`,
      estimated_roi_percent: (v) => `${v}%`,
      planning_risk_score: (v) => v <= 2 ? 'Low' : v <= 3 ? 'Moderate' : 'High',
      timeline_months: (v) => `${v} mo`,
      initial_capital_required: (v) => `£${v.toLocaleString()}`,
      conversion_cost_psf: (v) => `£${v}`,
      permitted_development: (v) => v ? '✓ Available' : '✗ Required',
      units_created: (v) => `${v} units`
    },
    chartType: 'planning_timeline',
    showMonteCarlo: false,
    showRiskMatrix: true,
    timeframeLabel: 'months',
    riskProfile: 'moderate_to_high'
  },

  [STRATEGY_TYPES.LAND_BANKING]: {
    displayName: 'Land Banking',
    shortName: 'LAND',
    icon: '🌱',
    color: 'teal',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    textColor: 'text-teal-800',
    accentColor: 'bg-teal-500',
    description: 'Hold land for future development or appreciation',
    primaryMetrics: ['current_value', 'future_value_estimate', 'holding_period_years', 'appreciation_rate'],
    metricLabels: {
      current_value: 'Current Value',
      future_value_estimate: 'Future Value',
      holding_period_years: 'Hold Period',
      appreciation_rate: 'Annual Growth',
      planning_prospect: 'Planning Prospects',
      holding_costs_annual: 'Holding Costs/yr',
      alternative_use_value: 'Alternative Use'
    },
    metricFormats: {
      current_value: (v) => `£${v.toLocaleString()}`,
      future_value_estimate: (v) => `£${v.toLocaleString()}`,
      holding_period_years: (v) => `${v} yrs`,
      appreciation_rate: (v) => `${v}%`,
      planning_prospect: (v) => v === 'high' ? '★★★ High' : v === 'medium' ? '★★ Medium' : '★ Low',
      holding_costs_annual: (v) => `£${v.toLocaleString()}`,
      alternative_use_value: (v) => `£${v.toLocaleString()}`
    },
    chartType: 'appreciation_projection',
    showMonteCarlo: false,
    showRiskMatrix: true,
    timeframeLabel: 'years',
    riskProfile: 'low_to_moderate'
  }
};

/**
 * Get strategy configuration by type
 */
export const getStrategyConfig = (strategyType) => {
  return STRATEGY_CONFIG[strategyType] || STRATEGY_CONFIG[STRATEGY_TYPES.BUY_TO_LET];
};

/**
 * Get color classes for strategy
 */
export const getStrategyColors = (strategyType) => {
  const config = getStrategyConfig(strategyType);
  return {
    bg: config.bgColor,
    border: config.borderColor,
    text: config.textColor,
    accent: config.accentColor
  };
};

/**
 * Format metric value based on strategy configuration
 */
export const formatMetricValue = (strategyType, metricKey, value) => {
  const config = getStrategyConfig(strategyType);
  const formatter = config.metricFormats[metricKey];
  if (formatter && value !== undefined && value !== null) {
    return formatter(value);
  }
  return value?.toString() || 'N/A';
};

/**
 * Get all available strategies for comparison
 */
export const getAllStrategies = () => {
  return Object.entries(STRATEGY_CONFIG).map(([key, config]) => ({
    type: key,
    ...config
  }));
};

/**
 * Signal colors for investment recommendation
 */
export const SIGNAL_COLORS = {
  strong_allocate: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: 'STRONG BUY' },
  allocate: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'BUY' },
  negotiate: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', label: 'NEGOTIATE' },
  monitor: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', label: 'MONITOR' },
  reject: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: 'REJECT' }
};

/**
 * Risk level colors
 */
export const RISK_COLORS = {
  low: { bg: 'bg-green-100', text: 'text-green-800', label: 'LOW' },
  low_to_moderate: { bg: 'bg-green-50', text: 'text-green-700', label: 'LOW-MOD' },
  moderate: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'MODERATE' },
  moderate_to_high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'MOD-HIGH' },
  elevated: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'ELEVATED' },
  high: { bg: 'bg-red-100', text: 'text-red-800', label: 'HIGH' }
};

export default STRATEGY_CONFIG;
